import type { RichTextContent } from '@graphcms/rich-text-types';
import type {
  HeadingFragment,
  ParagraphFragment,
} from '@seek/cmsu-cms-connect';
import {
  RichText,
  renderers,
  type NodeRendererType,
} from '@seek/cmsu-rich-text';
import { Box, Heading, List, Text } from 'braid-design-system';
import { vars } from 'braid-design-system/css';
import React from 'react';

import { toLevel } from '../../utils';

const handleAlignment = (
  align: ParagraphFragment['align'],
): 'center' | 'flexEnd' | undefined => {
  if (align === 'center') {
    return 'center';
  } else if (align === 'right') {
    return 'flexEnd';
  }

  return undefined;
};

type Props = Pick<ParagraphFragment, 'align' | 'tone' | 'size'> & {
  content: RichTextContent;
  marginBottom?: ParagraphFragment['marginBottom'];
};

export const Paragraph = ({
  content,
  align,
  tone,
  size,
  marginBottom,
}: Props) => {
  const paragraph: NodeRendererType['p'] = ({ children }) => (
    <Text
      component="p"
      tone={tone || undefined}
      align={align || undefined}
      size={size || undefined}
    >
      {children}
    </Text>
  );

  const listAlignment = handleAlignment(align);
  const isSmallText = size === 'small' || size === 'xsmall';

  const list: (
    type: 'bullet' | 'number',
  ) => NodeRendererType['ul'] | NodeRendererType['ol'] =
    (type) =>
    ({ children }) =>
      (
        // Since list does not support alignment, we're doing it in the Box component
        // As a result, we need to modify align as center, flexEnd or undefined
        <Box display="flex" justifyContent={listAlignment}>
          <List space="medium" tone={tone || undefined} type={type}>
            {children}
          </List>
        </Box>
      );

  const heading: (level: HeadingFragment['level']) => NodeRendererType['h1'] =
    (level) =>
    ({ children }) =>
      (
        <Heading level={toLevel(level)}>
          <Box
            marginTop={isSmallText ? 'medium' : 'xlarge'}
            style={{
              color: vars.foregroundColor[tone!] || undefined,
            }}
          >
            {children}
          </Box>
        </Heading>
      );

  /**
   * Overriding the heading renderers so they render as paragraphs as well
   */
  const paragraphRenderers: NodeRendererType = {
    ...renderers,
    p: paragraph,
    h1: heading(1),
    h2: heading(2),
    h3: heading(3),
    h4: heading(4),
    h5: paragraph,
    h6: paragraph,
    ul: list('bullet'),
    ol: list('number'),
  };

  return (
    <Box marginBottom={marginBottom || 'none'}>
      <RichText renderers={paragraphRenderers} content={content} />
    </Box>
  );
};
